import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, CheckCircle2, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";

interface SecurityCriterion {
  key: string;
  label: string;
  description: string;
  points: number;
  passed: boolean;
  detail: string;
  fixPath: string;
}

interface SecurityScoreResponse {
  data: {
    score: number;
    maxScore: number;
    passedCount: number;
    generatedAt: string;
    criteria: SecurityCriterion[];
  };
}

function getScoreTone(score: number) {
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-amber-600";
  return "text-rose-600";
}

export default function SecuritySettingsPage() {
  const [data, setData] = useState<SecurityScoreResponse["data"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiFetch<SecurityScoreResponse>("/v1/platform/security-score");
        if (!cancelled) {
          setData(response.data);
        }
      } catch (caughtError) {
        if (!cancelled) {
          setError(caughtError instanceof Error ? caughtError.message : "Failed to load security score.");
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scoreRatio = useMemo(() => {
    if (!data || data.maxScore <= 0) return 0;
    return Math.min(Math.max(data.score / data.maxScore, 0), 1);
  }, [data]);

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - circumference * scoreRatio;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-[var(--color-foreground)]">Security dashboard</h2>
          <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Tenant security posture from mounted auth, domain, API key, webhook, retention, consent, and audit-log evidence.
          </p>
        </div>
        {data ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Last evaluated {formatDateTime(data.generatedAt)}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex min-h-[280px] items-center justify-center rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="flex items-center gap-3 text-sm text-[var(--color-muted-foreground)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading tenant security posture...
          </div>
        </div>
      ) : null}

      {!isLoading && error ? (
        <div className="rounded-3xl border border-rose-500/20 bg-rose-500/5 p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 text-rose-600" />
            <div>
              <p className="font-medium text-[var(--color-foreground)]">Could not load the mounted security score.</p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{error}</p>
            </div>
          </div>
        </div>
      ) : null}

      {!isLoading && data ? (
        <>
          <section className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
              <div className="flex flex-col items-center text-center">
                <div className="relative h-36 w-36">
                  <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
                    <circle cx="70" cy="70" r="54" fill="none" stroke="currentColor" strokeWidth="12" className="text-[var(--color-border)]" />
                    <circle
                      cx="70"
                      cy="70"
                      r="54"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="12"
                      strokeLinecap="round"
                      className={getScoreTone(data.score)}
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-4xl font-semibold ${getScoreTone(data.score)}`}>{data.score}</span>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">of {data.maxScore}</span>
                  </div>
                </div>
                <p className="mt-4 text-sm font-medium text-[var(--color-foreground)]">
                  {data.passedCount}/{data.criteria.length} controls passing
                </p>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Conservative scoring only counts controls backed by current platform evidence.
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Passing points</p>
                <p className="mt-3 text-3xl font-semibold text-[var(--color-foreground)]">{data.score}</p>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Controls contributing to the current score.</p>
              </div>
              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Failing controls</p>
                <p className="mt-3 text-3xl font-semibold text-[var(--color-foreground)]">{data.criteria.length - data.passedCount}</p>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Each failed row includes a direct fix destination.</p>
              </div>
              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Substitution note</p>
                <p className="mt-3 text-sm font-medium text-[var(--color-foreground)]">Recent security events</p>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  The queue asked for failed-login counts, but the mounted truth source today is the platform security audit log.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-[var(--color-foreground)]">Checklist</h3>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Every row is tied to a live settings or compliance surface.
              </p>
            </div>

            <div className="space-y-3">
              {data.criteria.map((criterion) => (
                <div
                  key={criterion.key}
                  className="flex flex-col gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 lg:flex-row lg:items-start lg:justify-between"
                >
                  <div className="flex min-w-0 gap-3">
                    {criterion.passed ? (
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-[var(--color-foreground)]">{criterion.label}</p>
                        <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]">
                          {criterion.points} pts
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{criterion.description}</p>
                      <p className="mt-2 text-sm text-[var(--color-foreground)]">{criterion.detail}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 lg:justify-end">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        criterion.passed
                          ? "bg-emerald-500/10 text-emerald-700"
                          : "bg-amber-500/10 text-amber-700"
                      }`}
                    >
                      {criterion.passed ? "Pass" : "Needs attention"}
                    </span>
                    <Link
                      to={criterion.fixPath}
                      className="inline-flex items-center rounded-full border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] transition hover:bg-[var(--color-surface)]"
                    >
                      Fix
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
